import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { usePayNowStatus } from '@features/paynow/hooks/usePayNowStatus'
import { useOrganizationFeature } from '@features/settings/hooks/useOrganizationFeature'

import { EmberUserType, useEmberUserType } from '../../../../../contexts/emberUserTypeContext'
import { Scope } from '../../../../../enums/scope'
import { useOrganizationSource } from '../../../../../hooks'
import { useOrganizationUiSetup } from '../../../../../hooks/useOrganizationUiSetup'
import { isAuthorized } from '../../../../../utils/isAuthorized'
import { SettingsRoute } from '../../../enums/settingsRoute'
import { SettingsTabItem, UseSettingsTabItems } from '../types'
import { filterSettingsTabItems } from '../utils/filterSettingsTabItems'
import { useShouldDisplayInvoicingSettingsTab } from './useShouldDisplayInvoicingSettingsTab'

interface UseOrganizationSettingsTabItemsProps {
  withCardPaymentsBadge?: boolean
}

export const useOrganizationSettingsTabItems = ({
  withCardPaymentsBadge = false,
}: UseOrganizationSettingsTabItemsProps = {}): UseSettingsTabItems => {
  const { t } = useTranslation()
  const { userType } = useEmberUserType()
  const { shouldDisplayInvoicingSettingsTab } = useShouldDisplayInvoicingSettingsTab()
  const canSeeEInvoicing = isAuthorized(Scope.EDocumentRead)

  const ownerUserTypes: EmberUserType[] = ['Accountant', 'Business Owner']
  const userIsOrganizationOwner = ownerUserTypes.includes(userType)
  const { currentFeatures } = useOrganizationFeature()
  const { isUiSetupSupernavInvoicingAccounting } = useOrganizationUiSetup()

  const { isSourceZervant } = useOrganizationSource()

  // Getting paid by card requires being able to invoice, so this follows the invoicing settings
  // scope. E-invoicing-only plans (quadra_free, ebp_free, itool_free) aren't granted it.
  const shouldDisplayCardPaymentsTab = isSourceZervant && isAuthorized(Scope.InvoiceRead)

  const {
    hasPayNowSettings,
    isLoading: isPayNowStatusLoading,
    isError: isPayNowStatusError,
  } = usePayNowStatus({
    enabled: withCardPaymentsBadge && shouldDisplayCardPaymentsTab,
  })

  const shouldShowCardPaymentsBadge =
    withCardPaymentsBadge && !isPayNowStatusLoading && !isPayNowStatusError && hasPayNowSettings === false

  const items: SettingsTabItem[] = useMemo(
    () => [
      {
        route: SettingsRoute.Organization,
        text: t('settings.organization.profile'),
        scopes: Scope.AccountRead,
      },
      {
        route: SettingsRoute.OrganizationAccounting,
        childRoutes: [
          SettingsRoute.OrganizationAccountingVatReturn,
          SettingsRoute.OrganizationAccountingChartOfAccounts,
        ],
        text: t('settings.organization.accounting'),
        // TODO: Replace with a `scopes` property when all users are migrated to the new scope version
        hidden: !isAuthorized(Scope.SettingTaxWrite, true),
      },
      {
        route: SettingsRoute.OrganizationVatRates,
        text: t('settings.organization.vat_rates'),
        hidden: !isAuthorized(Scope.SettingVatWrite, true),
      },
      {
        route: SettingsRoute.OrganizationInvoicing,
        text: t('settings.organization.invoicing'),
        hidden: !shouldDisplayInvoicingSettingsTab,
      },
      {
        route: SettingsRoute.OrganizationEInvoicing,
        text: t('settings.organization.e_invoicing'),
        hidden: !canSeeEInvoicing,
      },
      {
        route: SettingsRoute.OrganizationCardPayments,
        text: t('settings.organization.card_payments'),
        badgeLabel: shouldShowCardPaymentsBadge ? t('settings.organization.card_payments.new_badge') : undefined,
        hidden: !shouldDisplayCardPaymentsTab,
      },
      {
        route: SettingsRoute.OrganizationUsers,
        text: t('settings.organization.users'),
        hidden: !isAuthorized([[Scope.UserRead], [Scope.UserWriteAccountant]], false, userIsOrganizationOwner),
      },
      {
        route: SettingsRoute.OrganizationSubscription,
        childRoutes: [SettingsRoute.OrganizationSubscriptionPlansSelection],
        text: t('settings.organization.subscriptions'),
        hidden:
          isUiSetupSupernavInvoicingAccounting ||
          !isAuthorized(Scope.OrganizationSubscriptionWrite, false, userIsOrganizationOwner),
      },
      {
        route: SettingsRoute.OrganizationAccessTokens,
        text: t('settings.organization.apiTokens'),
        hidden: !isAuthorized(Scope.TokensRead, false, userIsOrganizationOwner),
      },
      {
        route: SettingsRoute.OrganizationBetas,
        text: t('settings.organization.features'),
        badgeCounter: currentFeatures.length,
        hidden: !userIsOrganizationOwner || !isAuthorized(Scope.BetaRead),
      },
    ],
    [
      t,
      canSeeEInvoicing,
      shouldDisplayInvoicingSettingsTab,
      userIsOrganizationOwner,
      currentFeatures.length,
      shouldShowCardPaymentsBadge,
      isUiSetupSupernavInvoicingAccounting,
      shouldDisplayCardPaymentsTab,
    ],
  )

  const itemsFiltered = useMemo(() => {
    return filterSettingsTabItems(items, { isAuthorized })
  }, [items])

  return { items: itemsFiltered }
}
